import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";

import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { HubSettingsFooter, HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { HubUserSearchableMultiSelect } from "@/components/settings/HubUserSearchableMultiSelect";
import { ApiError } from "@/lib/api";
import { unorderedStringArraysEqual } from "@/lib/settingsCardDirty";
import {
  fetchNotificationTopicOwners,
  notificationsTopicOwnersQueryKey,
  replaceNotificationTopicOwners,
  type HubNotificationTopic,
  type NotificationTopicOwner,
} from "@/lib/notificationsApi";

export type NotificationTopicOwnerUserOption = {
  userId: string;
  email: string;
  displayName: string | null;
};

export type NotificationTopicOwnersSettingsSectionProps = {
  topic: HubNotificationTopic;
  title: string;
  hoverHint: string;
  canEdit: boolean;
  /** Query key for hub user options when `users` is not provided. */
  userOptionsQueryKey?: QueryKey;
  /** Fetcher for hub user options when `users` is not provided. */
  fetchUserOptions?: () => Promise<{ users: NotificationTopicOwnerUserOption[] }>;
  /** Preloaded users from the parent (skips the user-options query). */
  users?: NotificationTopicOwnerUserOption[];
  readOnlyHoverHint?: string;
};

export function NotificationTopicOwnersSettingsSection({
  topic,
  title,
  hoverHint,
  canEdit,
  userOptionsQueryKey,
  fetchUserOptions,
  users: usersFromParent,
  readOnlyHoverHint = "Module administrators can edit this topic owner group.",
}: NotificationTopicOwnersSettingsSectionProps): JSX.Element {
  const queryClient = useQueryClient();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  const ownersQuery = useQuery({
    queryKey: notificationsTopicOwnersQueryKey(topic),
    queryFn: () => fetchNotificationTopicOwners(topic),
  });

  const usersQuery = useQuery({
    queryKey: userOptionsQueryKey ?? ["notifications", "topic-owner-users", topic],
    queryFn: () => {
      if (fetchUserOptions == null) {
        return Promise.resolve({ users: [] as NotificationTopicOwnerUserOption[] });
      }
      return fetchUserOptions();
    },
    enabled: usersFromParent == null && fetchUserOptions != null,
  });

  useEffect(() => {
    setInitialized(false);
  }, [topic]);

  useEffect(() => {
    if (ownersQuery.data != null && !initialized) {
      setSelectedUserIds(ownersQuery.data.owners.map((owner) => owner.userId));
      setInitialized(true);
    }
  }, [ownersQuery.data, initialized]);

  const saveMutation = useMutation({
    mutationFn: () => replaceNotificationTopicOwners(topic, selectedUserIds),
    onSuccess: (data) => {
      queryClient.setQueryData(notificationsTopicOwnersQueryKey(topic), data);
      setSelectedUserIds(data.owners.map((owner: NotificationTopicOwner) => owner.userId));
      setInitialized(true);
    },
  });

  const users = usersFromParent ?? usersQuery.data?.users ?? [];
  const usersLoading = usersFromParent == null && usersQuery.isLoading;

  const persistedUserIds = useMemo(
    () => ownersQuery.data?.owners.map((owner) => owner.userId) ?? [],
    [ownersQuery.data?.owners],
  );

  const isDirty = useMemo(() => {
    if (!initialized || ownersQuery.data == null) {
      return false;
    }
    return !unorderedStringArraysEqual(selectedUserIds, persistedUserIds);
  }, [initialized, ownersQuery.data, persistedUserIds, selectedUserIds]);

  return (
    <HubSettingsSection title={title} hoverHint={hoverHint}>
      {ownersQuery.isLoading || usersLoading ? (
        <p className="text-sm text-slate-500">Loading hub users…</p>
      ) : ownersQuery.isError ? (
        <p className="text-sm text-rose-700" role="alert">
          {ownersQuery.error instanceof ApiError
            ? ownersQuery.error.message
            : "Could not load topic owners."}
        </p>
      ) : users.length === 0 ? (
        <p className="text-sm text-slate-600">No hub users available for selection.</p>
      ) : (
        <HubUserSearchableMultiSelect
          id={`notification-topic-owners-${topic}`}
          users={users}
          selectedUserIds={selectedUserIds}
          onSelectedUserIdsChange={setSelectedUserIds}
          disabled={!canEdit}
          placeholder="Select topic owners…"
          noneSelectedLabel="No topic owners"
        />
      )}

      <HubSettingsFooter>
        <div className="space-y-2">
          {saveMutation.isError ? (
            <p className="text-sm text-rose-700" role="alert">
              {saveMutation.error instanceof ApiError
                ? saveMutation.error.message
                : saveMutation.error instanceof Error
                  ? saveMutation.error.message
                  : "Could not save topic owners."}
            </p>
          ) : null}
          <HubSaveButton
            type="button"
            dirty={isDirty}
            disabled={!canEdit || ownersQuery.isLoading}
            blockedHint={readOnlyHoverHint}
            isLoading={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            Save topic owners
          </HubSaveButton>
        </div>
      </HubSettingsFooter>
    </HubSettingsSection>
  );
}
